// src/components/friendship/FriendshipVibeAssessmentPreview.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, CheckCircle, Clock, Target, Sparkles, 
  ArrowRight, Users, Heart, Shield, AlertCircle 
} from 'lucide-react';
import Card from '../shared/Card';
import Button from '../shared/Button';
import { useApp } from '../../context/AppContext';

const FriendshipVibeAssessmentPreview = () => {
  const { setCurrentView } = useApp();
  
  const assessmentFeatures = [
    {
      icon: Users,
      title: 'Discover Your Friendship DNA',
      description: 'Uncover your unique blend across 6 friendship archetypes',
    },
    {
      icon: AlertCircle,
      title: 'Identify Hidden Patterns',
      description: 'Spot toxic behaviours like clinginess, drama attraction, or possessiveness',
    },
    {
      icon: Heart,
      title: 'Assess Your Squad Health',
      description: 'Understand if your friend circle is helping or hindering your growth',
    },
    {
      icon: Target,
      title: 'Get Personalised Action Steps',
      description: 'Receive specific, practical steps based on YOUR unique patterns',
    },
  ];
  
  const archetypes = [
    { emoji: '⚓', name: 'The Anchor', desc: 'Reliable & steady' },
    { emoji: '⚡', name: 'The Catalyst', desc: 'Energy bringer' },
    { emoji: '🦉', name: 'The Sage', desc: 'Wise advisor' },
    { emoji: '🌐', name: 'The Connector', desc: 'Social networker' },
    { emoji: '🛡️', name: 'The Guardian', desc: 'Protective friend' },
    { emoji: '🪞', name: 'The Mirror', desc: 'Empathetic validator' },
  ];
  
  return (
    <section className="py-20 bg-gradient-to-br from-slate-800/30 to-teal-900/20 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="mx-auto mb-8 max-w-sm"
          >
            <img
              src="/images/friendship-assessment.png"
              alt="Friendship Assessment"
              className="w-full h-auto rounded-2xl shadow-2xl shadow-teal-500/20"
            />
          </motion.div>

          <div className="flex items-center justify-center gap-2 mb-4">
            <Brain className="w-6 h-6 text-teal-400" />
            <span className="text-teal-400 font-medium">The Friendship Vibe Check</span>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            More Than Just Another Quiz
          </h2>

          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            26 carefully designed questions that reveal your friendship patterns -
            <span className="text-coral-300 font-semibold"> both the good and the challenging.</span>
             No obvious "right" answers, no gaming the system.
          </p>
        </motion.div>
        
        {/* Main Assessment Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto mb-12"
        >
          <Card className="bg-gradient-to-br from-white/5 to-white/10 border-teal-500/30 p-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Left side - What you'll discover */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-yellow-400" />
                  What You'll Discover
                </h3>
                
                <div className="space-y-4">
                  {assessmentFeatures.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-10 h-10 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <feature.icon className="w-5 h-5 text-teal-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-medium mb-1">{feature.title}</h4>
                        <p className="text-sm text-slate-400">{feature.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
                
                {/* Time and privacy info */}
                <div className="flex items-center gap-4 pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Clock className="w-4 h-4 text-coral-400" />
                    <span>10-15 minutes</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Shield className="w-4 h-4 text-green-400" />
                    <span>No name or email needed</span>
                  </div>
                </div>
              </div>
              
              {/* Right side - Archetypes preview */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white">Your Friendship Archetypes</h3>
                
                <div className="grid grid-cols-2 gap-3">
                  {archetypes.map((archetype, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                      className="p-3 bg-white/5 rounded-lg border border-white/10 hover:border-teal-500/30 transition-all"
                    >
                      <div className="text-2xl mb-1">{archetype.emoji}</div>
                      <div className="text-white font-medium text-sm">{archetype.name}</div>
                      <div className="text-xs text-slate-400">{archetype.desc}</div>
                    </motion.div>
                  ))}
                </div>
                
                <div className="p-4 bg-gradient-to-r from-teal-500/10 to-coral-500/10 rounded-lg border border-teal-500/30">
                  <p className="text-sm text-white">
                    <span className="font-semibold">You're not just one type.</span> Most people are a mix, and the
                    Vibe Check shows which styles come up most in your answers. It is a self-report, so it reflects
                    how you see yourself today, not a fixed label.
                  </p>
                </div>
              </div>
            </div>
            
            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-8 text-center"
            >
              <Button
                size="lg"
                onClick={() => setCurrentView('vibe-assessment-intro')}
                className="bg-gradient-to-r from-teal-600 to-coral-600 hover:from-teal-700 hover:to-coral-700"
              >
                <Brain className="w-5 h-5 mr-2" />
                Take the Friendship Vibe Check
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <p className="text-xs text-slate-500 mt-3">
                Free • No email required • Takes 10-15 minutes
              </p>
            </motion.div>
          </Card>
        </motion.div>
        
        {/* Honest disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="p-6 bg-orange-500/10 rounded-lg border border-orange-500/30">
            <AlertCircle className="w-8 h-8 text-orange-400 mx-auto mb-3" />
            <h4 className="text-white font-semibold mb-2">Heads Up: This Gets Real</h4>
            <p className="text-sm text-slate-300">
              This assessment doesn't just tell you nice things. It identifies problematic patterns too - 
              like being clingy, controlling, or emotionally draining. But <span className="text-orange-300 font-medium"> these patterns are totally fixable once you're aware of them.</span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FriendshipVibeAssessmentPreview;